import React, {useEffect, useState, useCallback} from 'react';
import styled from 'styled-components';
import {useMediaQuery} from 'react-responsive';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';

import {useApi} from '../../lib/api';
import {useCredentials} from '../../lib/user';
import {useUrlState} from '../../lib/with-url-state';
import * as tagHelpers from '../../lib/tag-helpers';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Input from '../../components/Input';
import Pagination from '../../components/Pagination';
import ListScreen from '../../components/ListScreen';
import ListItem from '../../components/ListItem';
import TimeAgo from '../../components/TimeAgo';
import TagManagementModal from '../../components/TagManagementModal';
import {T} from '../../components/I18n';
import InventoryTabs from './InventoryTabs';
import InventoryItemModal from './InventoryItemModal';
import {INVENTORY_PATH, itemPath} from './paths';
import {
  PAGE_SIZE,
  usePagedResource,
  useDebouncedSearch,
  useInventoryFilterOptions,
  useTagFilterReady,
  getTagId,
  formatQty,
  withUnit,
  getLotLabel,
  AttentionIcon,
  ItemTags,
  Negative,
  RowAmount,
  PaginationContainer
} from './shared';

const defaultUrlState = {
  sort: 'NAME',
  stock: 'all',
  unit: 'all',
  form: 'all',
  tag: 'all'
};

function BalancesView() {
  const api = useApi();
  const credentials = useCredentials();
  const history = useHistory();
  const {search: queryString} = useLocation();
  const itemMatch = useRouteMatch(`${INVENTORY_PATH}/items/:itemId`);
  const [urlState, setUrlState] = useUrlState(defaultUrlState);
  const {searchText, setSearchText, search} = useDebouncedSearch();
  const {forms, tags, tagsLoaded, reloadTags} = useInventoryFilterOptions();
  const [taggingItem, setTaggingItem] = useState(null);
  //the same breakpoint ListItem uses for its options. on a phone the balance moves into the row's details, so the
  //details keep the width they were measured at
  const isSmallScreen = useMediaQuery({maxWidth: 500});

  const {sort, stock, unit, form, tag} = urlState;
  const tagId = getTagId(tags, tag);
  const ready = useTagFilterReady(tag, tagId, tagsLoaded, setUrlState);

  //balances are paged, searched and filtered by the api, since a company can stock far more items than a page holds
  const balances = usePagedResource(
    page => api.getInventoryBalances({
      search,
      sort,
      stock: stock === 'all' ? null : stock,
      unit: unit === 'all' ? null : unit,
      formId: form === 'all' ? null : form,
      tagId,
      page,
      size: PAGE_SIZE
    }),
    [search, sort, stock, unit, form, tagId],
    ready
  );
  const {setPage} = balances;

  //a new search or filter starts again from the first page
  useEffect(() => setPage(0), [search, sort, stock, unit, form, tagId]);

  const handleCloseItem = useCallback(() => history.push(INVENTORY_PATH + queryString), [history, queryString]);

  const handleSaveTags = async (item, selectedTags, allAvailableTags, tagsToDelete = []) => {
    const currentTagNames = tags.map(tagHelpers.getTagName);
    const result = await tagHelpers.saveTags(item, selectedTags, allAvailableTags, tagsToDelete, credentials,
      'inventory', currentTagNames);

    if (result.success) {
      setTaggingItem(null);
      reloadTags();
      balances.reload();
    }
  };

  const {loading, error, result, page} = balances;
  const items = (result && result.content) || [];

  return (
    <>
      <ListScreen
        title="Inventory"
        activeLink="Inventory"
        tabs={<InventoryTabs view="balances" />}
        placeholder="Search items or lots..."
        loading={!result && !error && <Loading fullScreen />}
        error={error && <Error fullScreen />}
        empty={search
          ? <T isolate>No items or lots match "<T variable="search">{search}</T>".</T>
          : 'There is no inventory to display. Balances show up here once forms with inventory questions are submitted.'}
        items={items}
        search={searchText}
        onSearch={e => setSearchText(e.target.value)}
        toolbarActions={[
          <Input id="inventory-sort" label="Sort by:" type="select" value={sort}
                 onChange={e => setUrlState({sort: e.target.value})}>
            <option value="NAME">Alphabetical</option>
            <option value="NAME_DESC">Reverse Alphabetical</option>
            <option value="UPDATED">Recently Updated</option>
            <option value="UPDATED_ASC">Least Recently Updated</option>
            <option value="BALANCE">Highest Balance</option>
            <option value="BALANCE_ASC">Lowest Balance</option>
          </Input>
        ]}
        toolbarFilters={[
          <Input id="inventory-stock" label="Filter:" type="select" value={stock}
                 onChange={e => setUrlState({stock: e.target.value})}>
            <option value="all">All Items</option>
            <option value="IN_STOCK">In Stock</option>
            <option value="BELOW_ZERO">Below Zero</option>
            <option value="EXCEPTIONS">Has Exceptions</option>
          </Input>,
          <Input id="inventory-unit" label="Unit:" type="select" value={unit}
                 onChange={e => setUrlState({unit: e.target.value})}>
            <option value="all">All Units</option>
            <option value="WEIGHT">Weight</option>
            <option value="VOLUME">Volume</option>
            <option value="COUNT">Count</option>
          </Input>,
          <Input id="inventory-form" label="Form:" type="select" value={form}
                 onChange={e => setUrlState({form: e.target.value})} style={{maxWidth: '200px', minWidth: '150px'}}>
            <option value="all">All Forms</option>
            {forms.map(f => <option key={f.id} value={f.id} title={f.name}>{f.name}</option>)}
          </Input>,
          <Input id="inventory-tag" label="Tag:" type="select" value={tag}
                 onChange={e => setUrlState({tag: e.target.value})} style={{maxWidth: '200px', minWidth: '120px'}}>
            <option value="all">All Tags</option>
            {tags.map(t => {
              const tagName = tagHelpers.getTagName(t);
              return <option key={tagName} value={tagName}>{tagName}</option>;
            })}
          </Input>
        ]}
        footer={result && result.totalElements > PAGE_SIZE && (
          <PaginationContainer>
            <Pagination
              page={page + 1}
              itemsPerPage={PAGE_SIZE}
              totalItems={result.totalElements}
              disabled={loading}
              onClick={nextPage => setPage(nextPage - 1)}
            />
          </PaginationContainer>
        )}>
        {(item, i) => (
          <ListItem
            key={`${item.itemId}-${(item.tags || []).length}`}
            first={i === 0}
            notice={!isSmallScreen && <RowAmount><Balance item={item} /></RowAmount>}
            options={[
              {title: 'View', to: itemPath(INVENTORY_PATH, item.itemId) + queryString},
              {title: 'Modify Tags', onClick: () => setTaggingItem(item)}
            ]}>
            <ItemContent>
              <strong>
                <T noTranslate>{item.itemName}</T>
                {item.openExceptions > 0 && <AttentionIcon title="Has open exceptions" />}
              </strong>
              <ItemSubtitle>
                {isSmallScreen && <span>Balance: <Balance item={item} /></span>}
                <span>{getLotLabel(item.lotCode)}</span>
                <span>
                  Last Updated: {item.lastModifiedDate ? <TimeAgo date={new Date(item.lastModifiedDate)} /> : 'Never'}
                </span>
                <ItemTags
                  tags={item.tags}
                  onSelect={tagName => setUrlState({tag: tagName})}
                  onEdit={() => setTaggingItem(item)}
                />
              </ItemSubtitle>
            </ItemContent>
          </ListItem>
        )}
      </ListScreen>
      <InventoryItemModal
        itemId={itemMatch ? itemMatch.params.itemId : null}
        onClose={handleCloseItem}
        onChange={balances.reload}
      />
      <TagManagementModal
        open={!!taggingItem}
        onClose={() => setTaggingItem(null)}
        item={taggingItem && tagHelpers.normalizeItemForEditing({...taggingItem, id: taggingItem.itemId})}
        itemName={taggingItem && taggingItem.itemName}
        availableTags={tags}
        entityType="inventory"
        onSave={handleSaveTags}
      />
    </>
  );
}

function Balance({item}) {
  const balance = withUnit(formatQty(item.qty), item.unit);
  return item.qty < 0 ? <Negative>{balance}</Negative> : balance;
}

//laid out like a row on the forms list: a bold title over the details and the row's tags
const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  min-width: 0;

  strong {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ItemSubtitle = styled.small`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  color: #666;

  > span {
    display: block;
  }
`;

export default BalancesView;
